// @givmo/app-admin — Owner "Join requests" inbox (EP-SUITE2).
//
// A section rendered on the Admin Team page for OWNERS ONLY (the parent gates on the Owner role AND
// the join-or-create flag, and the backend list route is itself Owner-gated — a Member 403s). It lists
// the company's join requests (PENDING first, from GET /admin/company/join-requests) — each row shows
// the requester (name + email), the app they said they need (`requested_app`, EP-SUITE8: their own
// note, or nothing; never the legacy `requested_product`, which was the sign-up screen's stamp), and
// the request date — with Approve (the choice of a Global Admin or apps, nothing pre-checked) /
// Reject (optional note) actions on PENDING rows via the shared <JoinRequestActionDialog>. Wiring
// mirrors the admins feature: TanStack useInfiniteQuery + tolerant extractList decode + throwOnError
// sdk calls + testid contract.

import { useMemo, useState } from 'react';
import { useInfiniteQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { sdk, isGivmoError, type CompanyJoinRequestOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { Inbox } from 'lucide-react';
import { APPROVE_WORDS } from '../admins/app-choice-words';
import {
  requesterName,
  requesterEmail,
  requestedApp,
  requestedAppLabel,
  formatRequestDate,
  isPendingRequest,
  statusLabel,
} from './join-requests-decode';
import { JoinRequestActionDialog, type JoinRequestActionMode } from './join-request-action-dialog';

const PREFIX = 'join-requests';
const PAGE_LIMIT = 10;

export const JOIN_REQUESTS_LIST_KEY = ['companies-list_company_join_requests', 'infinite'] as const;

async function fetchJoinRequestsPage(
  offset: number,
  signal: AbortSignal,
): Promise<CompanyJoinRequestOut[]> {
  const { data } = await sdk.companiesListCompanyJoinRequests({
    query: { 'page[limit]': PAGE_LIMIT, 'page[offset]': offset },
    signal,
    throwOnError: true,
  });
  return extractList<CompanyJoinRequestOut>(data);
}

type ActionState = { mode: JoinRequestActionMode; request: CompanyJoinRequestOut } | null;

export interface JoinRequestsSectionProps {
  /** The host's admin roster, re-read once an approval adds someone to it. */
  rosterKey?: readonly unknown[];
}

export function JoinRequestsSection({ rosterKey }: JoinRequestsSectionProps = {}) {
  const [action, setAction] = useState<ActionState>(null);
  const rosterKeys = useMemo(() => (rosterKey === undefined ? [] : [rosterKey]), [rosterKey]);

  const list = useInfiniteQuery({
    queryKey: JOIN_REQUESTS_LIST_KEY,
    queryFn: ({ pageParam, signal }: QueryFunctionContext<typeof JOIN_REQUESTS_LIST_KEY, number>) =>
      fetchJoinRequestsPage(pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  });

  const requests = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);
  const pendingCount = useMemo(() => requests.filter(isPendingRequest).length, [requests]);

  const columns: DataTableColumn<CompanyJoinRequestOut>[] = useMemo(
    () => [
      {
        id: 'requester',
        header: 'Requester',
        width: '40%',
        cell: (req) => (
          <div className="min-w-0">
            <div className="truncate font-w600 text-text-strong">{requesterName(req)}</div>
            {requesterEmail(req).length > 0 && (
              <div className="truncate text-s13 text-text-tertiary">{requesterEmail(req)}</div>
            )}
          </div>
        ),
      },
      {
        id: 'product',
        header: APPROVE_WORDS.askedForColumn,
        // Stacked, each fact keeps its column's word (the header row goes), and the buttons get a
        // line of their own: every request reads in the same shape, as the console's own
        // access-requests panel does.
        stacked: 'line' as const,
        stackedLabel: APPROVE_WORDS.askedForColumn,
        // A request that names no app keeps its dash in the desktop column, which needs one; stacked,
        // "Asked for —" reads oddly as a sentence, so the line goes, as an empty cell's would.
        stackedClassName: 'max-sm:has-[[data-no-app]]:hidden',
        cell: (req) =>
          requestedApp(req) === null ? (
            <span className="text-text-secondary" data-no-app="">
              {requestedAppLabel(req)}
            </span>
          ) : (
            <span className="text-text-secondary">{requestedAppLabel(req)}</span>
          ),
      },
      {
        id: 'requested',
        header: 'Requested',
        stacked: 'line' as const,
        stackedLabel: 'Requested',
        cell: (req) => (
          <span className="text-text-tertiary tabular-nums">
            {formatRequestDate(req.created_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        align: 'right' as const,
        width: '220px',
        stacked: 'line' as const,
        cell: (req) =>
          isPendingRequest(req) ? (
            <div className="flex items-center justify-end gap-8 max-sm:justify-start">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => setAction({ mode: 'reject', request: req })}
                testId={`${PREFIX}-row-${req.id}-reject`}
              >
                Reject
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                onClick={() => setAction({ mode: 'approve', request: req })}
                testId={`${PREFIX}-row-${req.id}-approve`}
              >
                Approve
              </Button>
            </div>
          ) : (
            <Badge tone="neutral">{statusLabel(req.status)}</Badge>
          ),
      },
    ],
    [],
  );

  return (
    <section className="flex flex-col gap-16" {...testIdProp('join-requests-section')}>
      <div className="flex items-center gap-8">
        <h2 className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Join requests
        </h2>
        {pendingCount > 0 && (
          <Badge tone="brand" testId="join-requests-pending-badge">
            {pendingCount} pending
          </Badge>
        )}
      </div>
      <p className="font-sans text-s14 text-text-tertiary">
        Teammates whose work email matches your company’s domain can request to join. Approve to add
        them as an admin.
      </p>

      <DataTable
        testId={`${PREFIX}-list`}
        columns={columns}
        rows={requests}
        rowKey={(r) => r.id}
        rowTestId={() => `${PREFIX}-row`}
        // Below `sm` each request is a stacked block (who, what they asked for, when, then Reject
        // and Approve), as the console's roster is (OM-13): at 390px the table scrolled sideways in
        // its card, and "Asked for" sat behind the scroll. From 640px up the table is unchanged; no
        // row action replaces the two buttons, so they keep their place and their ids.
        stackAt="sm"
        loading={list.isPending}
        error={
          list.isError
            ? isGivmoError(list.error)
              ? list.error.message
              : 'Failed to load join requests.'
            : undefined
        }
        onRetry={() => void list.refetch()}
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={Inbox} size={20} aria-hidden />}
            title="No join requests"
            description="When a teammate requests access to your company, it will show up here."
          />
        }
        footer={
          list.hasNextPage ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
              testId={`${PREFIX}-load-more`}
            >
              {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          ) : null
        }
      />

      <JoinRequestActionDialog
        open={action !== null}
        onOpenChange={(open) => {
          if (!open) setAction(null);
        }}
        mode={action?.mode ?? null}
        request={action?.request ?? null}
        rosterKeys={rosterKeys}
      />
    </section>
  );
}
